import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Autocomplete, Chip, FormControl, Icon, TextField } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import DialogBase from './DialogBase';
import ProgressBar from '../ProgressBar';
import ErrorMessage from '../ErrorMessage';

import { Organisatie } from '../../../models/organisatie/organisatie';
import { ListParams } from '../../../models/listParams';
import { planManagementService } from '../../../services/plan-management.service';
import { portfolioService } from '../../../services/portfolio.service';
import { userManagementService } from '../../../services/user-management.service';


interface DialogPortfolioAddOrganisatieProps {
  open: boolean;
  portfolioId?: string;
  excludedOrganizationIds?: string[];
  onClose: () => void;
  onSubmit: () => void;
}

interface OrganizationOption {
  id: string;
  label: string;
  group: 'Met gebied' | 'Zonder gebied';
}

const DialogPortfolioAddOrganisatie: React.FC<DialogPortfolioAddOrganisatieProps> = ({
  open,
  portfolioId,
  excludedOrganizationIds = [],
  onClose,
  onSubmit,
}) => {
  const navigate = useNavigate();

  const [options, setOptions] = useState<OrganizationOption[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<OrganizationOption[]>([]);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const excludedOrganizationIdsKey = JSON.stringify(excludedOrganizationIds);
  const excludedOrganizationIdsSet = useMemo(
    () => new Set<string>(JSON.parse(excludedOrganizationIdsKey) as string[]),
    [excludedOrganizationIdsKey]
  );

  const fetchOrganisaties = useCallback(async () => {
    setLoading(true);
    try {
      const storedOrganisatie = userManagementService.getStoredOrganisatie();

      const params: ListParams = { offset: 0, limit: 250 };
      const data = await planManagementService.getOrganisatiesToShare(params, false, { fetchAll: true });
      const organisaties: Organisatie[] = Array.isArray(data?.items) ? data.items : [];

      const seenIds = new Set<string>();
      const mappedOptions = organisaties
        .map((organisatie) => {
          if (!organisatie.id) {
            return null;
          }
          if (seenIds.has(organisatie.id)) {
            return null;
          }
          if (storedOrganisatie?.id && organisatie.id === storedOrganisatie.id) {
            return null;
          }
          if (excludedOrganizationIdsSet.has(organisatie.id)) {
            return null;
          }

          seenIds.add(organisatie.id);

          return {
            id: organisatie.id,
            label: organisatie.displayName || organisatie.name || organisatie.id,
            group: organisatie.organizationType === 'zonder-gebied' ? 'Zonder gebied' : 'Met gebied',
          } as OrganizationOption;
        })
        .filter((item): item is OrganizationOption => Boolean(item))
        .sort((a, b) => {
          if (a.group !== b.group) {
            return a.group.localeCompare(b.group, 'nl-NL');
          }
          return a.label.localeCompare(b.label, 'nl-NL');
        });

      setOptions(mappedOptions);
      setSelectedOptions([]);
      setSelectionError(null);
    }
    catch (error: any) {
      if (error?.message === 'Unable to refresh token') {
        navigate('/verlopen');
      }
      setError(error?.message || 'Er is een fout opgetreden');
    }
    finally {
      setLoading(false);
    }
  }, [excludedOrganizationIdsSet, navigate]);

  useEffect(() => {
    if (open) {
      fetchOrganisaties();
    }
    else {
      setSelectedOptions([]);
      setSelectionError(null);
    }
  }, [fetchOrganisaties, open]);

  const handleSubmit = async () => {
    if (!portfolioId) {
      return;
    }

    if (selectedOptions.length === 0) {
      setSelectionError('Selecteer minimaal 1 organisatie.');
      return;
    }

    try {
      setLoading(true);
      setSelectionError(null);

      const ids = selectedOptions.map((item) => item.id);
      await Promise.all(ids.map((organizationId) =>
        portfolioService.inviteOrganizationToPortfolio(portfolioId, organizationId)
      ));

      onSubmit();
      onClose();
      setSelectedOptions([]);
    }
    catch (error) {
      console.error('Error inviting organisatie:', error);
      setError('Er is een fout opgetreden');
    }
    finally {
      setLoading(false);
    }
  };

  return (
    <DialogBase
      open={open}
      title="Organisatie toevoegen"
      contentText="Nodig één of meerdere organisaties uit voor dit afstemmingsdossier."
      onClose={onClose}
      onConfirm={handleSubmit}
      confirmButtonText="Uitnodigen"
      confirmButtonIcon={<Icon>send</Icon>}
    >
      <section>
        <FormControl fullWidth required sx={{ my: 1 }}>
          <Autocomplete
            multiple
            options={options}
            value={selectedOptions}
            disabled={loading}
            noOptionsText="Geen keuzes beschikbaar"
            loadingText="Laden..."
            onChange={(_event, value) => {
              const normalized = Array.isArray(value) ? value : value ? [value] : [];
              setSelectedOptions(normalized);
              if (normalized.length > 0) {
                setSelectionError(null);
              }
            }}
            groupBy={(option) => option.group}
            getOptionLabel={(option) => option?.label || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            filterSelectedOptions
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip variant="filled" label={option.label} {...getTagProps({ index })} key={option.id} />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                required
                label="Organisaties toevoegen"
                variant="filled"
                placeholder="Zoek en selecteer één of meerdere organisaties"
                error={Boolean(selectionError)}
                helperText={selectionError || undefined}
              />
            )}
          />
        </FormControl>
      </section>

      { loading && (
        <ProgressBar />
      )}

      { error && (
        <ErrorMessage message={error} />
      )}
    </DialogBase>
  );
};

export default DialogPortfolioAddOrganisatie;
